import { FC, useCallback, useEffect, useState } from 'react';
import { useHookstate } from '@hookstate/core';
import { isMobile } from 'react-device-detect';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
    preloadBingoMarketingModal,
    type BingoMarketingModalProps,
} from '../../components/Header/BingoMarketingModal.lazy';
import { HeaderMobile } from '../../components/Header/mobile';
import { HeaderDesktop } from '../../components/Header/desktop';
import { useAccount } from '../../hooks/use-account';
import { useBackendPayload } from '../../hooks/use-backend';
import { useFormatCurrency } from '../../hooks/use-format-currency';
import { useBoughtTickets } from '../../hooks/use-bought-tickets';
import { getQuery } from '../../lib/query';
import {
    account,
    current,
    next,
    meta,
    openModal,
    getBalance,
    screenConfig as screenConfigState,
    toggleCurrency,
} from '../../stores/backend';

const MARKETING_MODAL_DO_NOT_SHOW_AGAIN_KEY = 'marketingModal.doNotShowAgain';

const onSettingsOpen = () => {
    openModal({ key: 'settings' });
};

const getContentfulHostBase = (): Pick<
    BingoMarketingModalProps,
    'contentfulHost' | 'contentfulBasePath'
> => {
    if (window.location.protocol === 'https:') {
        return {
            contentfulBasePath: '/contentful',
            contentfulHost: window.location.host,
        };
    }

    return {};
};

export const Header: FC = () => {
    const [isMax, setIsMax] = useState<boolean>(false);
    const [doNotShowAgainChecked, setDoNotShowAgainChecked] = useState<boolean>(
        () =>
            localStorage.getItem(MARKETING_MODAL_DO_NOT_SHOW_AGAIN_KEY) ===
            'true'
    );

    const { i18n } = useTranslation();
    const { backendPayload } = useBackendPayload();

    const { SESSIONID, ROOMID } = getQuery();
    const theme = useTheme();

    const $current = useHookstate(current);
    const $next = useHookstate(next);
    const $meta = useHookstate(meta);
    const $account = useHookstate(account);
    const $bought = useBoughtTickets($current);
    const { screenConfig, sideGames } = useHookstate(screenConfigState);

    const gameMode = $current.game_mode.value;
    const { cash, bonus, totalAccount } = useAccount($current, $meta, $account);
    const formatCurrency = useFormatCurrency($current, $meta);

    const currencyUnit =
        $next.currencies.value[$meta.currency.value]?.unit || 'cash';

    const { spaceId, environmentId, accessToken } =
        screenConfig.site.data.contentful;

    const hideCurrencyToggle =
        !screenConfig.display.enableCurrencyToggle.value ||
        current.game_currency.value === current.player_currency.value;

    const isAutoOpen = useCallback(() => {
        return (
            !!spaceId?.value && !!environmentId?.value && !!accessToken?.value
        );
    }, [spaceId, environmentId, accessToken]);

    const hasContentfulConfig =
        !!spaceId?.value && !!environmentId?.value && !!accessToken.value;

    useEffect(() => {
        if (hasContentfulConfig) {
            preloadBingoMarketingModal();
        }
    }, [hasContentfulConfig]);

    const marketingModalConfig = hasContentfulConfig
        ? ({
              contentfulSpace: spaceId?.value || '',
              contentfulEnvironment: environmentId?.value || '',
              contentfulAccessToken: accessToken?.value || '',
              ...getContentfulHostBase(),
              jSessionToken: SESSIONID,
              siteId: screenConfig.site.siteCode.value || '',
              roomId: ROOMID,
              currency:
                  $current.game_currency.value || $next.game_currency.value,
              cash: formatCurrency($account.player_cash.value),
              bonus: formatCurrency($account.player_bonus.value),
              balance: formatCurrency(getBalance()),
              refreshBalance: () => backendPayload.getAccountStatus(),
              theme: theme.marketingModal?.theme || 'default',
              locales: i18n.languages as string[],
              showDoNotShowAgain: true,
              doNotShowAgainChecked,
              onDoNotShowAgainChange: (checked: boolean) => {
                  setDoNotShowAgainChecked(checked);
                  localStorage.setItem(
                      MARKETING_MODAL_DO_NOT_SHOW_AGAIN_KEY,
                      checked + ''
                  );
              },
          } as BingoMarketingModalProps)
        : null;

    const onMarketingModalToggle = useCallback(() => {
        $meta.modals.marketing.set((value) => !value);
    }, [$meta.modals.marketing]);

    const onToggleScreen = () => {
        if (isMax) {
            document.exitFullscreen();
            setIsMax(false);

            return;
        }

        const element = document.body;

        element?.requestFullscreen();
        setIsMax(true);
    };

    useEffect(() => {
        if (
            (!$current.game_sid.value || !$meta.loaded.screenConfig.value) &&
            isAutoOpen() &&
            !doNotShowAgainChecked &&
            !$meta.modals.marketing.value
        ) {
            $meta.modals.marketing.set(true);
        }
    }, [
        $current.game_sid.value,
        $meta.loaded.screenConfig.value,
        $meta.modals.marketing,
        doNotShowAgainChecked,
        isAutoOpen,
    ]);

    if (
        !$current.game_sid.value ||
        !$meta.loaded.screenConfig.value ||
        (gameMode === 'GAME_MODE' && $bought === 0 && !$next.game_sid.value)
    ) {
        return null;
    }

    const Component = isMobile ? HeaderMobile : HeaderDesktop;

    return (
        <Component
            currencyUnit={currencyUnit}
            isMax={isMax}
            cash={cash}
            bonus={bonus}
            tokens={$account.player_fp_balance.value || 0}
            total={totalAccount}
            currency={$meta.currency.value}
            username={$current.player_alias.value}
            gameId={$current.game_sid.value}
            hideSideGames={sideGames.value.length === 0}
            hideCurrencyToggle={hideCurrencyToggle}
            marketingModalConfig={marketingModalConfig}
            isOpen={$meta.modals.marketing.value}
            onSettingsOpen={onSettingsOpen}
            onMarketingModalToggle={onMarketingModalToggle}
            onToggleCurrency={toggleCurrency}
            onToggleScreen={onToggleScreen}
        />
    );
};
